import { useState, useContext } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import AuthModal from "@/components/AuthModal";
import { AuthContext } from "@/App";
import { Sparkles, Brain, Code2, Mic, TrendingUp, Shield, Star, CheckCircle, Users, Mail, Phone, MessageSquare, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";

const LandingPage = () => {
  const [showAuth, setShowAuth] = useState(false);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Handle Enroll Now button click
  const handleEnrollClick = () => {
    if (user) {
      navigate('/checkout');
    } else {
      setShowAuth(true);
    }
  };

  const benefits = [
    {
      icon: Brain,
      title: "Critical Thinking",
      description: "AI and coding develop problem-solving skills and logical reasoning that help kids excel in all subjects."
    },
    {
      icon: Sparkles,
      title: "Future-Ready Skills",
      description: "Prepare your child for tomorrow's digital world with in-demand AI and programming knowledge."
    },
    {
      icon: TrendingUp,
      title: "Boost Confidence",
      description: "Building projects and creating with code gives kids a sense of accomplishment and self-confidence."
    },
    {
      icon: Code2,
      title: "Creative Expression",
      description: "Coding is the new literacy - it allows kids to create games, apps, and bring their ideas to life."
    },
    {
      icon: Users,
      title: "Collaborative Learning",
      description: "Learn teamwork and communication through group projects and peer interaction."
    },
    {
      icon: Shield,
      title: "Safe Environment",
      description: "Kid-friendly platform with age-appropriate content and supervised AI interactions."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Mitchell",
      role: "Parent",
      image: "https://i.pravatar.cc/150?img=1",
      rating: 5,
      text: "My 9-year-old daughter loves the AI teachers! She's more engaged than ever and actually asks to do her lessons. The voice interaction makes learning feel like talking to a friend."
    },
    {
      name: "David Chen",
      role: "Parent",
      image: "https://i.pravatar.cc/150?img=13",
      rating: 5,
      text: "As a software engineer, I wanted my kids to learn coding early. Kids Genie makes it fun and interactive. My son built his first game after just 4 weeks!"
    },
    {
      name: "Emma Thompson",
      role: "Parent",
      image: "https://i.pravatar.cc/150?img=5",
      rating: 5,
      text: "The progress tracking is amazing. I can see exactly what my child is learning and how they're improving. The AI adapts to their pace perfectly."
    },
    {
      name: "Michael Rodriguez",
      role: "Student, Age 11",
      image: "https://i.pravatar.cc/150?img=8",
      rating: 5,
      text: "I love talking to the AI teacher! It explains things in a way that's easy to understand and never gets impatient. Learning to code is so cool!"
    },
    {
      name: "Lisa Anderson",
      role: "Parent",
      image: "https://i.pravatar.cc/150?img=9",
      rating: 5,
      text: "Best investment in my child's education! The curriculum is well-structured and the AI teachers make complex concepts simple. Highly recommend!"
    },
    {
      name: "Sophie Williams",
      role: "Student, Age 10",
      image: "https://i.pravatar.cc/150?img=10",
      rating: 5,
      text: "I made my own AI project and showed it to my class! Everyone thought it was amazing. This is the best learning program ever!"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 glass-effect border-b border-white/20">
        <div className="container mx-auto px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img 
              src="/images/robot-genie-logo.png" 
              alt="Kids Genie" 
              className="w-16 h-16 object-contain"
            />
            <div>
              <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Kids Genie
              </span>
              <div className="text-xs font-medium text-gray-600 -mt-1">AI & Coding Academy</div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <a href="#benefits" className="text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors">Benefits</a>
            <a href="#courses" className="text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors">Courses</a>
            <a href="#pricing" className="text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors">Pricing</a>
            <a href="#testimonials" className="text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors">Testimonials</a>
            <a href="#faq" className="text-sm font-medium text-gray-700 hover:text-indigo-600 transition-colors">FAQ</a>
            <Button
              onClick={() => navigate('/contact')}
              variant="outline"
              size="sm"
              className="rounded-full border-2"
            >
              Contact Us
            </Button>
            <Button
              data-testid="header-login-btn"
              onClick={() => setShowAuth(true)}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-full transition-all duration-300 hover:scale-105"
            >
              Sign In
            </Button>
          </nav>
          <Button
            data-testid="header-login-btn-mobile"
            onClick={() => setShowAuth(true)}
            className="md:hidden bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-full"
          >
            Sign In
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-slide-in-up">
              <div className="inline-flex items-center gap-2 bg-indigo-100 text-indigo-700 px-4 py-2 rounded-full mb-6">
                <Sparkles className="w-4 h-4" />
                <span className="text-sm font-medium">AI-Powered Learning Platform</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Learn AI & Coding with
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">
                  {" "}Voice-Interactive AI Teachers
                </span>
              </h1>
              <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl">
                Our AI teachers speak, listen, and adapt to your child's learning pace. Perfect for kids aged 7-12 years.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  data-testid="hero-get-started-btn"
                  onClick={() => setShowAuth(true)}
                  size="lg"
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-xl"
                >
                  Start Learning Now
                </Button>
                <Button
                  onClick={() => navigate('/contact')}
                  size="lg"
                  variant="outline"
                  className="px-8 py-6 text-lg rounded-full border-2"
                >
                  Get in Touch
                </Button>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative animate-fade-in">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="/images/robot-genie-logo.png"
                  alt="Robot Genie Teacher"
                  className="w-full h-auto"
                />
              </div>
              {/* Floating stats */}
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-4">
                <div className="text-3xl font-bold text-indigo-600">10k+</div>
                <div className="text-sm text-gray-600">Happy Students</div>
              </div>
              <div className="absolute -top-6 -right-6 bg-white rounded-2xl shadow-xl p-4">
                <div className="text-3xl font-bold text-purple-600">4.9★</div>
                <div className="text-sm text-gray-600">Parent Rating</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section - New Attractive Video */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 relative overflow-hidden">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 opacity-50"></div>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-64 h-64 bg-yellow-300/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-blue-300/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="container mx-auto max-w-5xl relative z-10">
          <div className="text-center mb-8 sm:mb-12 animate-slide-in-up">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-4 sm:mb-6 shadow-lg">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 animate-spin-slow" />
              <span className="text-xs sm:text-sm font-bold">Watch Our Magic!</span>
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 animate-spin-slow" />
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold mb-3 sm:mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600">
                See Kids Genie in Action! 🎉
              </span>
            </h2>
            <p className="text-base sm:text-lg text-gray-700 max-w-2xl mx-auto px-4">
              Watch how our AI teachers make learning fun and exciting for kids!
            </p>
          </div>

          {/* Video Container with Attractive Border */}
          <div className="relative group animate-fade-in">
            {/* Decorative elements around video - Hidden on mobile */}
            <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 rounded-2xl sm:rounded-3xl blur-xl opacity-75 group-hover:opacity-100 transition-opacity duration-500 animate-pulse"></div>
            
            {/* Animated Text Overlays - Hidden on mobile, shown on desktop */}
            <div className="hidden lg:block absolute -top-16 left-8 bg-gradient-to-r from-green-400 to-blue-500 text-white px-6 py-3 rounded-2xl shadow-2xl animate-bounce-slow z-20">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🎮</span>
                <span className="font-bold text-lg">Learn Coding in a Fun Way!</span>
              </div>
            </div>

            <div className="hidden lg:block absolute top-12 -right-16 bg-gradient-to-r from-pink-500 to-purple-500 text-white px-6 py-3 rounded-2xl shadow-2xl animate-float delay-300 z-20">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🤖</span>
                <span className="font-bold text-lg">AI Teachers Make It Easy!</span>
              </div>
            </div>

            <div className="hidden lg:block absolute bottom-1/3 -left-20 bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-6 py-3 rounded-2xl shadow-2xl animate-bounce-slow delay-500 z-20">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🌟</span>
                <span className="font-bold text-lg">Build Amazing Projects!</span>
              </div>
            </div>

            <div className="hidden lg:block absolute -bottom-16 right-8 bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-2xl shadow-2xl animate-float delay-700 z-20">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🎯</span>
                <span className="font-bold text-lg">Voice Interactive Lessons!</span>
              </div>
            </div>
            
            {/* Video wrapper */}
            <div className="relative bg-white p-2 sm:p-3 rounded-2xl sm:rounded-3xl shadow-2xl">
              <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-inner">
                <video
                  className="w-full h-auto rounded-lg sm:rounded-xl"
                  controls
                  preload="metadata"
                  playsInline
                >
                  <source src="https://customer-assets.emergentagent.com/job_76c62706-9c22-40f7-8725-f8f677d0f64f/artifacts/2fy2n6yj_Video%20Project%2018.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>

            {/* Floating decorative elements - Hidden on mobile */}
            <div className="hidden sm:flex absolute -top-8 -left-8 w-16 h-16 bg-yellow-400 rounded-full items-center justify-center shadow-xl animate-bounce-slow">
              <span className="text-2xl">⭐</span>
            </div>
            <div className="hidden sm:flex absolute -bottom-8 -right-8 w-16 h-16 bg-pink-400 rounded-full items-center justify-center shadow-xl animate-bounce-slow delay-500">
              <span className="text-2xl">🚀</span>
            </div>
            <div className="hidden md:flex absolute top-1/2 -left-12 w-12 h-12 bg-blue-400 rounded-full items-center justify-center shadow-xl animate-float">
              <span className="text-xl">🎨</span>
            </div>
            <div className="hidden md:flex absolute top-1/4 -right-12 w-12 h-12 bg-purple-400 rounded-full items-center justify-center shadow-xl animate-float delay-700">
              <span className="text-xl">💡</span>
            </div>
          </div>

          {/* Call to Action below video */}
          <div className="text-center mt-8 sm:mt-12 animate-slide-in-up delay-300">
            <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 px-4">
              Ready to start your child's learning adventure?
            </p>
            <Button
              onClick={() => navigate('/demo-chat')}
              size="lg"
              className="bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 hover:from-purple-700 hover:via-pink-700 hover:to-blue-700 text-white px-6 sm:px-10 py-4 sm:py-6 text-base sm:text-lg rounded-full transition-all duration-300 hover:scale-110 shadow-2xl"
            >
              🎓 Try Free Demo with Geni!
            </Button>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-6 bg-white/50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Why AI & Coding Matter for Kids
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Equip your child with essential skills for the digital age and watch them thrive
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div
                  key={index}
                  className="p-6 rounded-2xl bg-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mb-4">
                    <Icon className="w-7 h-7 text-indigo-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-gray-900">{benefit.title}</h3>
                  <p className="text-sm text-gray-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section id="courses" className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Our Courses
            </h2>
            <p className="text-base text-gray-600">10-lesson interactive programs with our AI teacher - Learn at your own pace!</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="relative rounded-2xl overflow-hidden shadow-xl group">
              <img
                src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800"
                alt="AI Course"
                className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-indigo-900 via-indigo-900/60 to-transparent flex flex-col justify-end p-8">
                <div className="flex items-center gap-2 mb-3">
                  <Brain className="w-6 h-6 text-white" />
                  <span className="text-white font-medium">10 Lessons</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">AI for Kids</h3>
                <p className="text-sm text-white/90 mb-4">
                  Learn the basics of Artificial Intelligence through fun, interactive lessons.
                </p>
                <Button
                  onClick={handleEnrollClick}
                  className="bg-white text-indigo-600 hover:bg-gray-100 w-fit"
                >
                  Enroll Now
                </Button>
              </div>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl group">
              <img
                src="https://images.unsplash.com/photo-1515879218367-8466d910aaa4?w=800"
                alt="Coding Course"
                className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-purple-900 via-purple-900/60 to-transparent flex flex-col justify-end p-8">
                <div className="flex items-center gap-2 mb-3">
                  <Code2 className="w-6 h-6 text-white" />
                  <span className="text-white font-medium">10 Lessons</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">Coding Fundamentals</h3>
                <p className="text-sm text-white/90 mb-4">
                  Master programming basics with hands-on projects and AI guidance.
                </p>
                <Button
                  onClick={handleEnrollClick}
                  className="bg-white text-purple-600 hover:bg-gray-100 w-fit"
                >
                  Enroll Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-6 bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-yellow-400 text-gray-900 text-sm font-semibold rounded-full mb-4">
              💎 SPECIAL LAUNCH PRICING
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Choose Your Learning Adventure
            </h2>
            <p className="text-base text-gray-300 max-w-2xl mx-auto">
              Self-paced learning with our AI teacher. Master each lesson before moving on - no rushing, no waiting!
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8 mb-12">
            {/* Semester Pass - Single Course */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:border-cyan-400/50 transition-all duration-300">
              <div className="text-center mb-6">
                <span className="text-4xl mb-4 block">🎓</span>
                <h3 className="text-2xl font-bold text-white mb-2">Semester Pass</h3>
                <p className="text-gray-300 text-sm">Perfect for testing the waters</p>
              </div>
              
              <div className="text-center mb-6">
                <span className="text-5xl font-bold text-white">$39</span>
                <span className="text-gray-300 ml-2">one-time</span>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span><strong>Choice of AI OR Coding</strong> Course (10 Lessons)</span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span><strong>6 Months Access</strong> to AI Tutor</span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span><strong>Unlock-on-Mastery:</strong> Move as fast as you want</span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span><strong>10 Downloadable Workbooks</strong> - Yours forever!</span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-cyan-400 flex-shrink-0 mt-0.5" />
                  <span><strong>Standard Diploma</strong> upon completion</span>
                </li>
              </ul>

              <Button
                onClick={() => navigate('/checkout?plan=semester')}
                className="w-full py-6 bg-cyan-500 hover:bg-cyan-400 text-white font-bold text-lg"
              >
                Get Started
              </Button>
            </div>

            {/* Dual-Core Academy - Bundle (BEST SELLER) */}
            <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 backdrop-blur-lg rounded-2xl p-8 border-2 border-yellow-400 relative transform lg:scale-105 shadow-2xl">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-gray-900 px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                  🏆 BEST VALUE - SAVE $19
                </span>
              </div>
              
              <div className="text-center mb-6 pt-4">
                <span className="text-4xl mb-4 block">🚀</span>
                <h3 className="text-2xl font-bold text-white mb-2">Dual-Core Academy</h3>
                <p className="text-yellow-200 text-sm">Complete learning experience</p>
              </div>
              
              <div className="text-center mb-6">
                <span className="text-5xl font-bold text-white">$59</span>
                <span className="text-gray-300 ml-2">one-time</span>
                <div className="text-yellow-400 text-sm mt-1">
                  <span className="line-through text-gray-400">$78</span> Save $19!
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span><strong>BOTH AI & Coding Courses</strong> (20 Lessons total)</span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span><strong>12 Months Access</strong> - A full year!</span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span><strong>Priority AI:</strong> Faster response times</span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span><strong>20 Downloadable Workbooks</strong> - Yours forever!</span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span><strong>"Honors" Gold Diploma</strong> upon completion</span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span><strong>Weekly Parent Progress Report</strong> via email</span>
                </li>
              </ul>

              <Button
                onClick={() => navigate('/checkout?plan=bundle')}
                className="w-full py-6 bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-gray-900 font-bold text-lg shadow-lg"
              >
                Get Both Courses
              </Button>
            </div>

            {/* Family Pack */}
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20 hover:border-green-400/50 transition-all duration-300">
              <div className="text-center mb-6">
                <span className="text-4xl mb-4 block">👨‍👩‍👧‍👦</span>
                <h3 className="text-2xl font-bold text-white mb-2">Family Pack</h3>
                <p className="text-gray-300 text-sm">For households with 2 kids</p>
              </div>
              
              <div className="text-center mb-6">
                <span className="text-5xl font-bold text-white">$59</span>
                <span className="text-gray-300 ml-2">one-time</span>
                <div className="text-green-400 text-sm mt-1">
                  2nd child at 50% off!
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span><strong>2 Separate Student Accounts</strong></span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span><strong>Choice of AI OR Coding</strong> per child</span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span><strong>6 Months Access</strong> for each child</span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span><strong>Individual Progress Tracking</strong></span>
                </li>
                <li className="flex items-start gap-3 text-white">
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <span><strong>Personalized Diplomas</strong> for each child</span>
                </li>
              </ul>

              <Button
                onClick={() => navigate('/checkout?plan=family')}
                className="w-full py-6 bg-green-500 hover:bg-green-400 text-white font-bold text-lg"
              >
                Enroll Both Kids
              </Button>
            </div>
          </div>

          {/* Sibling Add-On */}
          <div className="text-center bg-white/5 backdrop-blur-lg rounded-xl p-6 border border-white/10 max-w-2xl mx-auto">
            <p className="text-white mb-2">
              <span className="text-2xl mr-2">👋</span>
              <strong>Already enrolled one child?</strong>
            </p>
            <p className="text-gray-300 text-sm mb-4">
              Add a sibling profile for just <span className="text-green-400 font-bold">$25</span> (available in your parent dashboard)
            </p>
            <p className="text-yellow-400 text-xs">
              ✨ Each child gets their own private AI Tutor, separate progress tracking, and personalized diploma!
            </p>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-8 mt-12 pt-8 border-t border-white/10">
            <div className="flex items-center gap-2 text-white/80">
              <Shield className="w-5 h-5 text-green-400" />
              <span className="text-sm">Secure Payment</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Star className="w-5 h-5 text-yellow-400" />
              <span className="text-sm">Lifetime Workbook Access</span>
            </div>
            <div className="flex items-center gap-2 text-white/80">
              <Users className="w-5 h-5 text-cyan-400" />
              <span className="text-sm">1000+ Happy Students</span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-6 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              What Parents & Students Say
            </h2>
            <p className="text-base text-gray-600">Real feedback from our learning community</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-14 h-14 rounded-full object-cover"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-xs text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{testimonial.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-base text-gray-600">
              Everything you need to know about Kids Genie
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="item-1" className="bg-white border border-gray-200 rounded-2xl px-6 shadow-sm hover:shadow-md transition-shadow">
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-indigo-600">
                What is Kids Genie AI & Coding Academy?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pt-2">
                Kids Genie is an innovative online learning platform designed specifically for children aged 7-12 years. We combine cutting-edge AI technology with interactive coding education to provide a unique learning experience. Our voice-interactive AI teachers can speak and listen to students, making learning engaging and personalized. We offer comprehensive 10-week courses in both AI fundamentals and coding basics, helping children develop critical skills for the digital age.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-2" className="bg-white border border-gray-200 rounded-2xl px-6 shadow-sm hover:shadow-md transition-shadow">
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-indigo-600">
                Why should my child learn AI and coding at this age?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pt-2">
                Ages 7-12 are crucial developmental years when children's brains are most receptive to learning new skills. Early exposure to AI and coding helps develop critical thinking, problem-solving abilities, and logical reasoning that benefit all areas of learning. In today's digital world, understanding technology isn't optional—it's essential. By starting young, children gain confidence, creativity, and future-ready skills that will give them a significant advantage in their academic and professional lives. Plus, learning through voice-interactive AI makes it fun and engaging, not intimidating!
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-3" className="bg-white border border-gray-200 rounded-2xl px-6 shadow-sm hover:shadow-md transition-shadow">
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-indigo-600">
                How do the voice-interactive AI teachers work?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pt-2">
                Our AI teachers use advanced voice technology to create natural, conversational learning experiences. Students can speak directly to their AI teacher using their device's microphone, ask questions, request explanations, and receive verbal feedback—just like talking to a human tutor. The AI adapts to each child's learning pace, provides patient explanations, and never gets tired or frustrated. This creates a safe, supportive environment where children feel comfortable asking questions and exploring concepts without pressure or judgment.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-4" className="bg-white border border-gray-200 rounded-2xl px-6 shadow-sm hover:shadow-md transition-shadow">
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-indigo-600">
                What do the courses include?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pt-2">
                We offer two comprehensive 10-week courses: <strong>AI for Kids</strong> covers artificial intelligence basics, machine learning concepts, real-world AI applications, and hands-on AI projects. <strong>Coding Fundamentals</strong> teaches programming basics, problem-solving with code, creating games and apps, and building complete projects. Each course includes weekly lessons, interactive exercises, voice-guided instruction from AI teachers, progress tracking, and achievement certificates. Lessons are designed to be engaging, age-appropriate, and build upon each other progressively.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="item-5" className="bg-white border border-gray-200 rounded-2xl px-6 shadow-sm hover:shadow-md transition-shadow">
              <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-indigo-600">
                Is Kids Genie safe for my child?
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pt-2">
                Absolutely! Safety is our top priority. All content is carefully curated to be age-appropriate for 7-12 year-olds. Our AI interactions are supervised and designed specifically for educational purposes. We maintain a secure, kid-friendly environment with no ads, inappropriate content, or unsupervised chat features. Parents have full access to progress tracking and can monitor their child's learning journey. Our platform complies with children's online privacy regulations, and we never share personal information with third parties.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-indigo-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Ready to Start Your Learning Journey?
          </h2>
          <p className="text-base text-white/90 mb-8">
            Join thousands of kids already learning with AI teachers
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              data-testid="cta-get-started-btn"
              onClick={() => setShowAuth(true)}
              size="lg"
              className="bg-white text-indigo-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-xl"
            >
              Get Started Now
            </Button>
            <Button
              onClick={() => navigate('/demo-chat')}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full"
            >
              Try Free Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 bg-gray-900 text-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/images/robot-genie-logo.png" 
                  alt="Kids Genie" 
                  className="w-12 h-12 object-contain"
                />
                <div>
                  <div className="text-xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                    Kids Genie AI, LLC
                  </div>
                  <div className="text-xs text-gray-400 -mt-1">AI & Coding Academy</div>
                </div>
              </div>
              <p className="text-sm text-gray-400 mb-4">
                Empowering young minds with AI and coding skills through voice-interactive learning.
              </p>
              <p className="text-xs text-gray-500">
                Delaware LLC | Operating Globally
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#benefits" className="hover:text-white transition-colors">Benefits</a></li>
                <li><a href="#courses" className="hover:text-white transition-colors">Courses</a></li>
                <li><a href="#testimonials" className="hover:text-white transition-colors">Testimonials</a></li>
                <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
                <li><a href="/contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Legal</h4>
              <ul className="space-y-2 text-sm text-gray-400 mb-6">
                <li><a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
              <h4 className="font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>info@kidsgenie-ai-academy.com</span>
                </li>
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>support@kidsgenie-ai-academy.com</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+961 76388467 (Lebanon)</span>
                </li>
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>+1 302 520 2688 (USA)</span>
                </li>
                <li className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>Beirut, Lebanon</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8">
            {/* Social Media Links */}
            <div className="flex justify-center gap-6 mb-6">
              <a 
                href="https://www.facebook.com/kidsgenieai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-indigo-600 rounded-full flex items-center justify-center transition-colors duration-300"
                aria-label="Facebook"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              
              <a 
                href="https://www.instagram.com/kidsgenieai/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-pink-600 rounded-full flex items-center justify-center transition-colors duration-300"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              
              <a 
                href="https://www.youtube.com/@KidsGenieAICodingAcademy" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors duration-300"
                aria-label="YouTube"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
              
              <a 
                href="https://www.linkedin.com/company/110046382/admin/dashboard/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-blue-600 rounded-full flex items-center justify-center transition-colors duration-300"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              
              <a 
                href="https://x.com/abbas224" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-gray-600 rounded-full flex items-center justify-center transition-colors duration-300"
                aria-label="X (Twitter)"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              
              <a 
                href="https://tiktok.com/@kidsgenieai" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-gray-800 hover:bg-gray-900 rounded-full flex items-center justify-center transition-colors duration-300"
                aria-label="TikTok"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </a>
            </div>
            
            {/* Copyright */}
            <div className="text-center">
              <p className="text-sm text-gray-500">
                © 2025 Kids Genie AI, LLC. All rights reserved. | Delaware, USA
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="sm:max-w-md">
          <AuthModal 
            onClose={() => setShowAuth(false)} 
            onAuthSuccess={() => {
              setShowAuth(false);
              navigate('/checkout');
            }}
            skipNavigate={true}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LandingPage;
